import React, { useState, useEffect } from 'react';
import { User, AuthProviderConfig } from '../types';
import { socialLogin, adminLogin, verifyMfa, getAuthProviders, startMfaSetup, requestEmailMfa } from '../services/apiService';
import { config } from '../services/config';
import { Button } from './Button';
import { FcGoogle } from 'react-icons/fc';
import { BsMicrosoft, BsLinkedin } from 'react-icons/bs';

interface LoginViewProps {
  onLogin: (user: User) => void;
}

export const LoginView: React.FC<LoginViewProps> = ({ onLogin }) => {
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [mfaStep, setMfaStep] = useState<{ userId: string; message: string; method?: 'totp' | 'email_otp'; allowEmailOtp?: boolean } | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [loginError, setLoginError] = useState<string | null>(null);
  const [providers, setProviders] = useState<AuthProviderConfig[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    { title: <>AI for Work, Life & <br /> <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">Social Impact</span></>, desc: "A ready-to-use AI platform designed to automate your daily tasks." },
    { title: <>Redefine Your <br /> <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">Daily Rhythm</span></>, desc: "Experience a seamless flow where work meets life." },
    { title: <>Empower Your <br /> <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">Every Move</span></>, desc: "Transform how you manage time from impulsive ideas to structured success." }
  ];

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const all = await getAuthProviders();
        setProviders(all.filter(p => p.isEnabled));
      } catch (e) {
        console.error("Failed to load providers", e);
        setProviders([
           { id: 'google', name: 'Google', isEnabled: true },
           { id: 'microsoft', name: 'Microsoft', isEnabled: true }
        ]);
      }
    };
    fetchProviders();

    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    
    // Check for OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const userData = urlParams.get('user');
    const error = urlParams.get('error');
    
    if (error) {
      setLoginError(`Authentication failed: ${error}`);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname + '#/login');
    } else if (token && userData) {
      try {
        const user = JSON.parse(userData);
        // Store token
        localStorage.setItem('pf_token', token);
        onLogin(user);
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname + '#/');
      } catch (e) {
        console.error('Failed to parse OAuth user data:', e);
        setLoginError('Authentication failed: Invalid response data');
        window.history.replaceState({}, document.title, window.location.pathname + '#/login');
      }
    }
    
    return () => clearInterval(timer);
  }, []);

  const handleProviderLogin = async (providerId: string) => {
    setIsLoading(providerId);
    setLoginError(null);
    try {
      // Check if using mock data
      if (config.useMockData) {
        const mockEmail = `user.${providerId}@example.com`;
        const mockName = `User ${providerId}`;
        const mockAvatar = `https://ui-avatars.com/api/?name=${mockName}&background=random`;
        
        const response = await socialLogin(providerId, mockEmail, mockName, mockAvatar);
        
        if (response.mfaRequired) {
          setMfaStep({ userId: response.userId, message: response.message || 'MFA Required', method: response.method, allowEmailOtp: response.allowEmailOtp });
        } else if (response.user) {
          onLogin(response.user);
        }
      } else {
        // Real OAuth flow
        const response = await fetch(`${config.apiBaseUrl}/auth/oauth/${providerId}`);
        if (!response.ok) {
          throw new Error('Failed to initiate OAuth');
        }
        const data = await response.json();
        window.location.href = data.authorizationUrl;
      }
    } catch (error: any) {
      console.error("Login failed", error);
      // Display the specific blocking message if present
      if (error.message && error.message.includes("Contact the administrator")) {
        setLoginError(error.message);
      } else {
        setLoginError(`Login with ${providerId} failed. Please try again.`);
      }
    } finally {
      setIsLoading(null);
    }
  };

  const handleMfaVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaStep || !mfaCode) return;
    setIsLoading('mfa');
    setMfaError('');
    try {
      const response = await verifyMfa(mfaStep.userId, mfaCode, mfaStep.method);
      if (response.user) {
        onLogin(response.user);
      }
    } catch (error) {
      setMfaError("Invalid code. Try '123456'");
    } finally {
      setIsLoading(null);
    }
  };

  const handleEmailOtpRequest = async () => {
    if (!mfaStep) return;
    try {
      setIsLoading('email');
      await requestEmailMfa(mfaStep.userId);
      setMfaStep({ ...mfaStep, method: 'email_otp', message: 'Check your email for the 6-digit code.' });
    } catch (e) {
      setMfaError('Failed to send email code');
    } finally {
      setIsLoading(null);
    }
  };

  const getProviderIcon = (id: string) => {
    if (id.includes('google')) return <FcGoogle className="text-xl" />;
    if (id.includes('microsoft')) return <BsMicrosoft className="text-xl text-[#00a4ef]" />;
    if (id.includes('linkedin')) return <BsLinkedin className="text-xl text-[#0077b5]" />;
    return <span className="text-xl font-bold text-slate-400">{id.charAt(0).toUpperCase()}</span>;
  };

  if (mfaStep) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-sm w-full animate-[fadeIn_0.3s_ease-out]">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
               <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
            </div>
            <h2 className="text-2xl font-bold text-slate-800">Two-Step Verification</h2>
            <p className="text-sm text-slate-500 mt-2">{mfaStep.message}</p>
          </div>
          <form onSubmit={handleMfaVerify} className="space-y-4">
            <input autoFocus type="text" placeholder="Enter 6-digit code" className="w-full text-center text-2xl tracking-widest px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500/50 outline-none" value={mfaCode} onChange={e => setMfaCode(e.target.value.replace(/\D/g,'').slice(0,6))} />
            {mfaError && <p className="text-xs text-red-500 text-center mt-2">{mfaError}</p>}
            <Button type="submit" className="w-full justify-center" isLoading={isLoading === 'mfa'}>Verify</Button>
            {mfaStep.allowEmailOtp && mfaStep.method === 'totp' && (
              <button type="button" onClick={handleEmailOtpRequest} disabled={isLoading === 'email'} className="w-full text-xs text-indigo-600 hover:text-indigo-700 font-semibold mt-2">
                {isLoading === 'email' ? 'Sending code...' : 'Use email code instead'}
              </button>
            )}
            <button type="button" onClick={() => setMfaStep(null)} className="w-full text-xs text-slate-400 hover:text-slate-600 mt-4">Cancel Login</button>
          </form>
          <div className="mt-6 p-3 bg-blue-50 text-blue-700 text-xs rounded-lg text-center"><strong>Dev Hint:</strong> Use code <code>123456</code>.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex w-full bg-white font-sans">
      {/* Left Pane */}
      <div className="hidden lg:flex w-1/2 bg-[#f8f9fd] flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-100/50 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-100/50 rounded-full blur-3xl -ml-20 -mb-20 pointer-events-none"></div>
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <img 
              src="/assets/logo.svg" 
              alt="Kanbanboard" 
              className="h-12 w-auto"
              onError={(e) => {
                // Fallback to text logo if SVG fails to load
                e.currentTarget.style.display = 'none';
                const fallback = e.currentTarget.nextElementSibling;
                if (fallback) fallback.classList.remove('hidden');
              }}
            />
            <div className="h-12 w-12 bg-indigo-600 rounded-lg flex items-center justify-center hidden">
              <span className="text-white font-bold text-lg">P</span>
            </div>
          </div>
        </div>
        <div className="relative z-10 max-w-lg min-h-[220px]">
          <div key={currentSlide} className="animate-[fadeIn_0.5s_ease-out]">
            <h1 className="text-5xl font-extrabold text-slate-900 leading-tight mb-6 tracking-tight">{slides[currentSlide].title}</h1>
            <p className="text-lg text-slate-500 leading-relaxed">{slides[currentSlide].desc}</p>
          </div>
          <div className="flex gap-2 mt-8">
            {slides.map((_, idx) => (
              <button key={idx} onClick={() => setCurrentSlide(idx)} className={`h-1.5 rounded-full transition-all duration-300 ${currentSlide === idx ? 'w-8 bg-indigo-600' : 'w-2 bg-slate-300 hover:bg-slate-400'}`} />
            ))}
          </div>
        </div>
        <div className="relative z-10 flex items-center justify-between w-full text-[13px] text-slate-400 font-medium pt-8 mt-12 border-t border-slate-200/60">
          <span>© 2026 Kanbanboard.org</span>
          <div className="flex items-center gap-6">
            <a 
              href="https://kanbanboard.org/privacy-policy" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-indigo-600 transition-colors"
            >
              Privacy Policy
            </a>
            <a 
              href="https://kanbanboard.org/terms-of-service" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-indigo-600 transition-colors"
            >
              Terms of Service
            </a>
          </div>
        </div>
      </div>
      
      {/* Right Pane */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 relative">
        {/* Mobile Logo - Only visible on small screens */}
        <div className="absolute top-8 left-8 lg:hidden">
          <img 
            src="/assets/logo.svg" 
            alt="Kanbanboard" 
            className="h-10 w-auto"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const fallback = e.currentTarget.nextElementSibling;
              if (fallback) fallback.classList.remove('hidden');
            }}
          />
          <div className="h-10 w-10 bg-indigo-600 rounded-lg flex items-center justify-center hidden">
            <span className="text-white font-bold">P</span>
          </div>
        </div>
        
        <div className="w-full max-w-sm space-y-10 animate-[fadeIn_0.3s_ease-out]">
          <div className="text-center">
            <h2 className="text-[32px] font-extrabold text-slate-900 tracking-tight leading-tight">Welcome back</h2>
            <p className="mt-3 text-slate-500 font-medium">Please sign in to access your dashboard.</p>
          </div>

          {loginError && (
             <div className="bg-red-50 border border-red-100 text-red-700 px-4 py-3.5 rounded-2xl text-sm flex items-start gap-3 shadow-sm animate-pulse">
                <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                <span className="font-medium leading-snug">{loginError}</span>
             </div>
          )}

          <div className="grid grid-cols-1 gap-4 pt-2">
            {providers.length > 0 ? providers.map(p => (
              <button 
                key={p.id}
                onClick={() => handleProviderLogin(p.id)} 
                disabled={!!isLoading} 
                className="group relative w-full flex justify-center items-center gap-4 px-6 py-4 border-2 border-slate-100/80 shadow-sm text-sm font-bold rounded-2xl text-slate-700 bg-white hover:border-indigo-500/40 hover:bg-slate-50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed transform active:scale-[0.98]"
              >
                {isLoading === p.id ? (
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-indigo-600 font-bold">Connecting...</span>
                  </div>
                ) : (
                  <>
                    <span className="transform group-hover:scale-110 transition-transform duration-300">
                      {getProviderIcon(p.id)}
                    </span>
                    <span className="tracking-tight">Continue with {p.name}</span>
                  </>
                )}
              </button>
            )) : (
              <div className="text-center p-8 bg-slate-50 rounded-[32px] border-2 border-dashed border-slate-200 shadow-inner">
                <p className="text-slate-400 text-sm font-bold italic text-center w-full">
                  No login providers available.
                </p>
                <p className="text-slate-400/70 text-xs mt-2 font-medium italic text-center w-full">
                  Please contact your system administrator.
                </p>
              </div>
            )}
          </div>

          <div className="mt-8 text-center pt-6 border-t border-slate-100/60">
            <p className="text-[13px] text-slate-500 font-medium leading-relaxed">
              Looking for more detail or enterprise options?<br />
              <a 
                href="https://app.kanbanboard.org/#/public/lead-form" 
                className="inline-flex items-center gap-1.5 text-indigo-600 font-bold hover:text-indigo-700 transition-colors mt-1 underline decoration-2 underline-offset-4 decoration-indigo-200 hover:decoration-indigo-500"
              >
                Send us your query
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </a>
            </p>
          </div>

          {window.location.hostname === 'localhost' && (
            <div className="mt-6 text-center">
              <button
                onClick={() => window.location.hash = '#/dev-login'}
                className="text-sm text-amber-600 hover:text-amber-700 font-semibold flex items-center gap-2 mx-auto"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
                Developer Login
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};